import { useTranslation } from '@govard/i18n-core/react';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Spin,
  Switch,
  TimePicker,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import type { ReactElement } from 'react';
import { useState } from 'react';

import {
  useTelegramOutboundMutation,
  useTelegramOutboundSettings,
} from '../hooks/useTelegramOutbound.js';
import {
  useTelegramPrivacyExportDownload,
  useTelegramPrivacyOperation,
} from '../hooks/useTelegramPrivacy.js';
import { TelegramLinkPanel } from './TelegramLinkPanel.js';

const { Text, Title } = Typography;

export function TelegramOutboundPanel(): ReactElement {
  const { t } = useTranslation();
  const query = useTelegramOutboundSettings();
  const mutation = useTelegramOutboundMutation();
  const data = query.data ?? {
    available: false,
    capabilities: { approvalResults: false, dailyDigest: false },
    approvalResult: {
      type: 'approval_result' as const,
      enabled: false,
      locale: 'ru' as const,
      timezone: null,
      localMinuteOfDay: null,
      nextDueAt: null,
      stateVersion: null,
    },
    dailyDigest: {
      type: 'daily_digest' as const,
      enabled: false,
      locale: 'ru' as const,
      timezone: 'Europe/Moscow',
      localMinuteOfDay: 540,
      nextDueAt: null,
      stateVersion: null,
    },
  };
  const privacy = useTelegramPrivacyOperation();
  const download = useTelegramPrivacyExportDownload();
  const [privacyKind, setPrivacyKind] = useState<'export' | 'delete' | null>(null);
  const [confirmation, setConfirmation] = useState('');

  const runPrivacy = async () => {
    if (privacyKind === null) return;
    const operation = await privacy.mutateAsync({ kind: privacyKind });
    if (privacyKind === 'export') {
      const value = await download.mutateAsync(operation.id);
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }),
      );
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'telegram-private-export.json';
      anchor.click();
      URL.revokeObjectURL(url);
    }
    setPrivacyKind(null);
    setConfirmation('');
  };

  if (query.isLoading) return <Spin aria-label={t('crm.settings.telegram.loading')} />;
  if (query.isError)
    return <Alert type="error" title={t('crm.settings.telegram.load_error')} showIcon />;

  const updateResult = (enabled: boolean) =>
    mutation.mutate({
      type: 'approval_result',
      enabled,
      expectedStateVersion: data.approvalResult.stateVersion,
      locale: data.approvalResult.locale,
    });
  const updateDigest = (patch: {
    enabled?: boolean;
    locale?: 'ru' | 'en';
    timezone?: string;
    minute?: number;
  }) =>
    mutation.mutate({
      type: 'daily_digest',
      enabled: patch.enabled ?? data.dailyDigest.enabled,
      expectedStateVersion: data.dailyDigest.stateVersion,
      locale: patch.locale ?? data.dailyDigest.locale,
      timezone: patch.timezone ?? data.dailyDigest.timezone ?? 'Europe/Moscow',
      localMinuteOfDay: patch.minute ?? data.dailyDigest.localMinuteOfDay ?? 540,
    });

  return (
    <Card variant="borderless" aria-label={t('crm.settings.telegram.title')}>
      <Title level={5}>{t('crm.settings.telegram.title')}</Title>
      <Text type="secondary">{t('crm.settings.telegram.private_only')}</Text>
      <div className="mt-5 flex flex-col gap-4">
        <TelegramLinkPanel />
        {!data.available ? (
          <Alert type="info" title={t('crm.settings.telegram.unavailable')} showIcon />
        ) : null}
        <Card size="small">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Text strong>{t('crm.settings.telegram.results')}</Text>
              <br />
              <Text type="secondary">{t('crm.settings.telegram.results_description')}</Text>
            </div>
            <Switch
              aria-label={t('crm.settings.telegram.results')}
              checked={data.approvalResult.enabled}
              loading={mutation.isPending}
              disabled={!data.available || !data.capabilities.approvalResults}
              onChange={updateResult}
            />
          </div>
        </Card>
        <Card size="small">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Text strong>{t('crm.settings.telegram.digest')}</Text>
              <br />
              <Text type="secondary">{t('crm.settings.telegram.digest_description')}</Text>
            </div>
            <Switch
              aria-label={t('crm.settings.telegram.digest')}
              checked={data.dailyDigest.enabled}
              loading={mutation.isPending}
              disabled={!data.available || !data.capabilities.dailyDigest}
              onChange={(enabled) => updateDigest({ enabled })}
            />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Select
              disabled={!data.available}
              aria-label={t('crm.settings.telegram.language')}
              value={data.dailyDigest.locale}
              options={[
                { value: 'ru', label: t('crm.settings.telegram.locale_ru') },
                { value: 'en', label: t('crm.settings.telegram.locale_en') },
              ]}
              onChange={(locale: 'ru' | 'en') => updateDigest({ locale })}
            />
            <Select
              disabled={!data.available}
              aria-label={t('crm.settings.telegram.timezone')}
              value={data.dailyDigest.timezone ?? 'Europe/Moscow'}
              options={[
                { value: 'Europe/Moscow', label: 'Europe/Moscow' },
                { value: 'Asia/Yekaterinburg', label: 'Asia/Yekaterinburg' },
                { value: 'UTC', label: 'UTC' },
              ]}
              onChange={(timezone) => updateDigest({ timezone })}
            />
            <TimePicker
              disabled={!data.available}
              aria-label={t('crm.settings.telegram.time')}
              format="HH:mm"
              minuteStep={5}
              value={dayjs()
                .startOf('day')
                .add(data.dailyDigest.localMinuteOfDay ?? 540, 'minute')}
              onChange={(value) =>
                value && updateDigest({ minute: value.hour() * 60 + value.minute() })
              }
            />
          </div>
          {!data.capabilities.dailyDigest ? (
            <Alert
              className="mt-3"
              type="info"
              title={t('crm.settings.telegram.rollout_disabled')}
              showIcon
            />
          ) : null}
          {data.dailyDigest.nextDueAt ? (
            <Text type="secondary" className="mt-3 block">
              {t('crm.settings.telegram.next_send', {
                date: new Intl.DateTimeFormat(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }).format(new Date(data.dailyDigest.nextDueAt)),
              })}
            </Text>
          ) : null}
        </Card>
        <Card size="small" title={t('crm.settings.telegram.privacy_title')}>
          <Text type="secondary">{t('crm.settings.telegram.privacy_description')}</Text>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button onClick={() => setPrivacyKind('export')}>
              {t('crm.settings.telegram.export_private')}
            </Button>
            <Button danger onClick={() => setPrivacyKind('delete')}>
              {t('crm.settings.telegram.delete_private')}
            </Button>
          </div>
        </Card>
      </div>
      {mutation.isError ? (
        <Alert
          className="mt-4"
          type="error"
          title={t('crm.settings.telegram.save_error')}
          showIcon
        />
      ) : null}
      <Modal
        open={privacyKind !== null}
        title={
          privacyKind === 'delete'
            ? t('crm.settings.telegram.delete_title')
            : t('crm.settings.telegram.export_title')
        }
        okButtonProps={{
          danger: privacyKind === 'delete',
          disabled: confirmation !== t('crm.settings.telegram.confirm_phrase'),
          loading: privacy.isPending || download.isPending,
        }}
        okText={t('crm.settings.telegram.confirm_action')}
        cancelText={t('shared.common.actions.cancel')}
        onCancel={() => {
          setPrivacyKind(null);
          setConfirmation('');
        }}
        onOk={() => void runPrivacy()}
      >
        <Alert
          type={privacyKind === 'delete' ? 'warning' : 'info'}
          showIcon
          title={
            privacyKind === 'delete'
              ? t('crm.settings.telegram.delete_warning')
              : t('crm.settings.telegram.export_description')
          }
        />
        <Text className="mt-4 block">
          {t('crm.settings.telegram.type_phrase', {
            phrase: t('crm.settings.telegram.confirm_phrase'),
          })}
        </Text>
        <Input
          className="mt-2"
          aria-label={t('crm.settings.telegram.confirmation')}
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
        />
      </Modal>
    </Card>
  );
}
